// frontend/src/components/Modals/CreateManualOrderModal.jsx
// Modal para crear orden manual cuando el cliente confirma sin enviar OC formal

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Alert,
  Card,
  CardBody,
} from 'reactstrap';
import { ordersAPI } from '../../services/api';

const CreateManualOrderModal = ({ isOpen, toggle, quote, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    use_internal_number: true,
    order_number: '',
    confirmation_method: 'email',
    confirmation_date: new Date().toISOString().split('T')[0],
    confirmation_reference: '',
    notes: '',
  });

  // Reset form cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setFormData({
        use_internal_number: true,
        order_number: '',
        confirmation_method: 'email',
        confirmation_date: new Date().toISOString().split('T')[0],
        confirmation_reference: '',
        notes: '',
      });
      setError(null);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = () => {
    setError(null);
    
    // Validaciones
    if (!formData.use_internal_number && !formData.order_number.trim()) {
      setError('Debe ingresar un número de orden o usar número interno');
      return;
    }
    
    if (!formData.confirmation_method) {
      setError('Debe seleccionar el medio de confirmación');
      return;
    }
    
    if (!formData.confirmation_date) {
      setError('Debe ingresar la fecha de confirmación');
      return;
    }
    
    // ✅ NO crear la orden aquí, solo pasar los datos al siguiente modal
    const manualOrderData = {
      use_internal_number: formData.use_internal_number,
      order_number: formData.use_internal_number ? '' : formData.order_number.trim(),
      confirmation_method: formData.confirmation_method,
      confirmation_date: formData.confirmation_date,
      confirmation_reference: formData.confirmation_reference.trim(),
      notes: formData.notes.trim(),
    };
    
    console.log('📦 Datos de orden manual capturados (sin crear aún):', manualOrderData);
    
    // Cerrar este modal
    toggle();
    
    // Pasar datos al siguiente paso
    if (onSuccess) {
      onSuccess(manualOrderData);
    }
  };


  const confirmationMethods = [
    { value: 'email', label: 'Correo Electrónico', icon: 'ni-email-83' },
    { value: 'phone', label: 'Llamada Telefónica', icon: 'ni-mobile-button' },
    { value: 'whatsapp', label: 'WhatsApp', icon: 'ni-chat-round' },
    { value: 'verbal', label: 'Verbal/Presencial', icon: 'ni-single-02' },
    { value: 'other', label: 'Otro', icon: 'ni-tag' },
  ];

  if (!quote) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      toggle={toggle}
      size="lg"
      backdrop="static"
      keyboard={false}
      className="modal-dialog-centered"
    >
      <ModalHeader 
        toggle={toggle}
        className="bg-gradient-warning border-0"
      >
        <div>
          <h5 className="mb-0 text-white font-weight-bold">
            <i className="ni ni-fat-add me-2"></i>
            Crear Orden Manual
          </h5>
          <small className="text-white opacity-8 d-block mt-1">
            Para clientes que confirman sin enviar OC formal
          </small>
        </div>
      </ModalHeader>

      <ModalBody className="p-4">
        {error && (
          <Alert color="danger" className="shadow-sm">
            <span className="alert-icon"><i className="ni ni-support-16"></i></span>
            <span className="alert-text">{error}</span>
          </Alert>
        )}

        {/* Resumen de la cotización */}
        <Card className="shadow-sm border-0 mb-4 bg-gradient-secondary">
          <CardBody className="p-3">
            <Row>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Propuesta</p>
                <h6 className="text-white mb-0">{quote.proposal_number} v{quote.version}</h6>
              </Col>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Cliente</p>
                <h6 className="text-white mb-0">{quote.client_name}</h6>
              </Col>
            </Row>
            <hr className="horizontal light my-2" />
            <Row>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Monto</p>
                <h5 className="text-white mb-0">
                  ${parseFloat(quote.subtotal || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                  <small className="text-white opacity-8 ms-2">+ IVA</small>
                </h5>
              </Col>
              <Col md="6">
                <p className="text-xs text-white text-uppercase mb-1">Servicio</p>
                <p className="text-white text-sm mb-0">{quote.service_type || quote.category || 'N/A'}</p>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Alert color="warning" className="shadow-sm mb-4">
          <span className="alert-icon"><i className="ni ni-bell-55"></i></span>
          <span className="alert-text">
            <strong>Nota:</strong> Esta orden se creará sin PDF del cliente. 
            Asegúrate de tener evidencia de la confirmación (correo, mensaje, etc.)
          </span>
        </Alert>

        <Form>
          {/* Número de Orden */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2">
              <h6 className="mb-0 text-dark">
                <i className="ni ni-tag me-2 text-primary"></i>
                Número de Orden
              </h6>
            </div>
            <div className="card-body p-3">
              <FormGroup check className="mb-3">
                <Input
                  type="checkbox"
                  id="use_internal_number"
                  name="use_internal_number"
                  checked={formData.use_internal_number}
                  onChange={handleChange}
                />
                <Label check for="use_internal_number" className="text-dark">
                  Generar número interno automático
                  <small className="text-muted d-block">
                    Formato: INT-2026-0001
                  </small>
                </Label>
              </FormGroup>
              
              {!formData.use_internal_number && (
                <FormGroup className="mb-0">
                  <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                    Número de Orden del Cliente <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="order_number"
                    className="form-control-alternative"
                    value={formData.order_number}
                    onChange={handleChange}
                    placeholder="Ej: OC-2026-001"
                  />
                </FormGroup>
              )}
            </div>
          </div>

          {/* Confirmación del Cliente */}
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light py-2">
              <h6 className="mb-0 text-dark">
                <i className="ni ni-check-bold me-2 text-success"></i>
                Confirmación del Cliente
              </h6>
            </div>
            <div className="card-body p-3">
              <Row>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                      Medio de Confirmación <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="confirmation_method"
                      className="form-control-alternative"
                      value={formData.confirmation_method}
                      onChange={handleChange}
                    >
                      {confirmationMethods.map(method => (
                        <option key={method.value} value={method.value}>
                          {method.label}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup className="mb-3">
                    <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                      Fecha de Confirmación <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="date"
                      name="confirmation_date"
                      className="form-control-alternative"
                      value={formData.confirmation_date}
                      onChange={handleChange}
                    />
                  </FormGroup>
                </Col>
              </Row>
              
              <FormGroup className="mb-0">
                <Label className="form-control-label text-uppercase text-xs font-weight-bold">
                  Referencia de Confirmación
                </Label>
                <Input
                  type="textarea"
                  name="confirmation_reference"
                  className="form-control-alternative"
                  value={formData.confirmation_reference}
                  onChange={handleChange}
                  placeholder="Ej: Email de Juan Pérez del 03/02/2026 confirmando el servicio"
                  rows="2"
                />
                <small className="text-muted d-block mt-1">
                  Describe brevemente cómo y quién confirmó la orden
                </small>
              </FormGroup>
            </div>
          </div>

          {/* Notas */}
          <FormGroup className="mb-0">
            <Label className="form-control-label text-uppercase text-xs font-weight-bold">
              Notas Adicionales
            </Label>
            <Input
              type="textarea"
              name="notes"
              className="form-control-alternative"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Notas o comentarios adicionales (opcional)"
              rows="2"
            />
          </FormGroup>
        </Form>
      </ModalBody>

      <ModalFooter className="border-0 bg-light">
        <Button 
          color="secondary" 
          onClick={toggle} 
          disabled={loading}
        >
          <i className="ni ni-fat-remove me-2"></i>
          Cancelar
        </Button>
        <Button 
          color="warning" 
          onClick={handleSubmit} 
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Creando...
            </>
          ) : (
            <>
              <i className="ni ni-check-bold me-2"></i>
              Crear Orden Manual
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default CreateManualOrderModal;
